import React, { useMemo, useState } from 'react';
import { Meta, Display, Chip } from '../../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../../components/states';
import { useApi } from '../../lib/api';
import ScheduleShell, { ScheduleSection } from './ScheduleShell';

/**
 * ScheduleBloom - oś czasu kwitnienia kwiatów Twojego ogrodu.
 *
 * Endpoint:
 *   GET /api/plots/all-with-details - dla wszystkich beds + plant_*
 *
 * Filtruje rośliny z:
 *   - category zaczynającym się od 'flower_'
 *   - bloom_season niepustym
 */

const MONTHS = [
  { n: 1,  s: 'I',   l: 'styczeń' },
  { n: 2,  s: 'II',  l: 'luty' },
  { n: 3,  s: 'III', l: 'marzec' },
  { n: 4,  s: 'IV',  l: 'kwiecień' },
  { n: 5,  s: 'V',   l: 'maj' },
  { n: 6,  s: 'VI',  l: 'czerwiec' },
  { n: 7,  s: 'VII', l: 'lipiec' },
  { n: 8,  s: 'VIII',l: 'sierpień' },
  { n: 9,  s: 'IX',  l: 'wrzesień' },
  { n: 10, s: 'X',   l: 'październik' },
  { n: 11, s: 'XI',  l: 'listopad' },
  { n: 12, s: 'XII', l: 'grudzień' },
];

const MONTH_NAMES = {
  'styczeń': 1, 'luty': 2, 'marzec': 3, 'kwiecień': 4, 'maj': 5,
  'czerwiec': 6, 'lipiec': 7, 'sierpień': 8, 'wrzesień': 9,
  'październik': 10, 'listopad': 11, 'grudzień': 12,
};

const SEASON_NAMES = {
  'wiosna': [3, 4, 5],
  'lato': [6, 7, 8],
  'jesień': [9, 10, 11],
  'zima': [12, 1, 2],
};

const COLOR_MAP = {
  'czerwony': '#c06a4a',
  'różowy': '#e8a8b8',
  'biały': '#f0ead6',
  'żółty': '#dcb464',
  'pomarańczowy': '#e89559',
  'fioletowy': '#a880c0',
  'niebieski': '#7e9bc4',
  'liliowy': '#b4a0c8',
  'kremowy': '#e0d8b8',
  'amarantowy': '#c8688c',
  'bordowy': '#883050',
};

function parseBloomSeason(str) {
  if (!str) return [];
  const cleaned = str.replace(/\(.*?\)/g, '').trim().toLowerCase();
  if (!cleaned) return [];

  // Sezony jako słowo
  for (const [season, months] of Object.entries(SEASON_NAMES)) {
    if (cleaned.includes(season)) return months;
  }

  // Zakres "miesiąc-miesiąc"
  if (cleaned.includes('-')) {
    const [start, end] = cleaned.split('-').map(s => s.trim());
    const startN = MONTH_NAMES[start];
    const endN = MONTH_NAMES[end];
    if (startN && endN) {
      const result = [];
      let m = startN;
      while (true) {
        result.push(m);
        if (m === endN) break;
        m = m === 12 ? 1 : m + 1;
        if (result.length > 12) break;
      }
      return result;
    }
  }

  // Pojedynczy miesiąc
  for (const [name, n] of Object.entries(MONTH_NAMES)) {
    if (cleaned === name || cleaned.startsWith(name + ' ')) return [n];
  }
  return [];
}

function bloomColor(flower_color) {
  if (!flower_color) return 'var(--t-sage)';
  const lower = flower_color.toLowerCase();
  for (const [name, hex] of Object.entries(COLOR_MAP)) {
    if (lower.includes(name)) return hex;
  }
  return 'var(--t-sage)';
}

export default function ScheduleBloom() {
  const [hoveredMonth, setHoveredMonth] = useState(null);
  const { data: plots, loading, error } = useApi('/api/plots/all-with-details');

  const bloomingPlants = useMemo(() => {
    if (!plots) return [];
    const out = [];
    plots.forEach(p => {
      (p.beds || []).forEach(b => {
        if (b.plant_category?.startsWith('flower_') && b.bloom_season) {
          out.push({
            id: b.id,
            name: b.plant_name,
            variety: b.plant_variety,
            plot_name: p.name,
            row_number: b.row_number,
            bloom_season: b.bloom_season,
            flower_color: b.flower_color,
            months: parseBloomSeason(b.bloom_season),
          });
        }
      });
    });
    return out.filter(p => p.months.length > 0);
  }, [plots]);

  const currentMonth = new Date().getMonth() + 1;

  if (error?.status === 401) {
    return <ScheduleShell><ErrorState error={error} /></ScheduleShell>;
  }
  if (loading) return <ScheduleShell><LoadingState /></ScheduleShell>;

  return (
    <ScheduleShell
      eyebrow="Kwitnienie"
      title="Oś czasu kwitnienia"
      subtitle="Każda kolorowa kropka to miesiąc, w którym kwitnie roślina z Twojego ogrodu. Kliknij miesiąc, żeby zobaczyć co wtedy kwitnie."
    >
      {bloomingPlants.length === 0 ? (
        <EmptyState
          title="Brak kwiatów z datą kwitnienia"
          description="Dodaj kwiaty do grządek w katalogu roślin — automatycznie pojawią się tutaj z osią kwitnienia."
        />
      ) : (
        <>
          {/* Stats */}
          <div style={{
            display: 'flex', gap: 20, flexWrap: 'wrap',
            padding: '16px 20px', borderRadius: 12,
            background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
            marginBottom: 24, fontSize: 13,
          }}>
            <span style={{ color: 'var(--t-text-dim)' }}>
              <strong style={{ color: 'var(--t-text)' }}>{bloomingPlants.length}</strong> kwiatów z osią kwitnienia
            </span>
            <span style={{ color: 'var(--t-text-dim)' }}>
              Aktualnie ({MONTHS[currentMonth - 1].l}): <strong style={{ color: 'var(--t-sage)' }}>
                {bloomingPlants.filter(p => p.months.includes(currentMonth)).length}
              </strong> kwitnie
            </span>
          </div>

          {/* Gantt timeline */}
          <ScheduleSection title="Cykl roczny">
            <div style={{
              overflowX: 'auto',
              border: '1px solid var(--t-line)',
              borderRadius: 12,
              background: 'var(--t-bg-elev)',
            }}>
              <div style={{
                minWidth: 720,
                display: 'grid',
                gridTemplateColumns: '220px repeat(12, 1fr)',
              }}>
                {/* Header months */}
                <div style={{
                  padding: '12px 14px',
                  fontFamily: 'var(--t-mono)', fontSize: 10,
                  letterSpacing: '.18em', textTransform: 'uppercase',
                  color: 'var(--t-text-mute)',
                  borderBottom: '1px solid var(--t-line)',
                }}>Roślina</div>
                {MONTHS.map(m => (
                  <button
                    key={m.n}
                    type="button"
                    onClick={() => setHoveredMonth(hoveredMonth === m.n ? null : m.n)}
                    style={{
                      padding: '12px 4px',
                      fontFamily: 'var(--t-mono)', fontSize: 10,
                      letterSpacing: '.12em',
                      color: m.n === currentMonth ? 'var(--t-sage)' : 'var(--t-text-mute)',
                      background: hoveredMonth === m.n ? 'rgba(168, 196, 158, 0.08)' : 'transparent',
                      borderBottom: '1px solid var(--t-line)',
                      borderLeft: 'none', borderRight: 'none', borderTop: 'none',
                      borderRadius: 0,
                      cursor: 'pointer', textAlign: 'center',
                      fontWeight: m.n === currentMonth ? 600 : 400,
                    }}
                  >{m.s}</button>
                ))}

                {/* Rows */}
                {bloomingPlants.map(p => (
                  <React.Fragment key={p.id}>
                    <div style={{
                      padding: '10px 14px',
                      borderBottom: '1px solid var(--t-line)',
                      fontSize: 13, color: 'var(--t-text)',
                      lineHeight: 1.3,
                    }}>
                      <div style={{ letterSpacing: '-.01em' }}>
                        {p.name}{p.variety && <span style={{ color: 'var(--t-text-dim)', fontSize: 12 }}> ({p.variety})</span>}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--t-text-mute)', marginTop: 2 }}>
                        {p.plot_name} · Rząd {p.row_number}
                      </div>
                    </div>
                    {MONTHS.map(m => {
                      const blooming = p.months.includes(m.n);
                      return (
                        <div key={m.n} style={{
                          padding: '14px 6px',
                          borderBottom: '1px solid var(--t-line)',
                          background: hoveredMonth === m.n ? 'rgba(168, 196, 158, 0.04)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {blooming && (
                            <div style={{
                              width: '100%', height: 6, borderRadius: 3,
                              background: bloomColor(p.flower_color),
                              opacity: m.n === currentMonth ? 1 : 0.7,
                            }} />
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </ScheduleSection>

          {/* Wybrany miesiąc - detale */}
          {hoveredMonth && (
            <ScheduleSection title={`Kwitnienie w ${MONTHS[hoveredMonth - 1].l}`}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 12,
              }}>
                {bloomingPlants
                  .filter(p => p.months.includes(hoveredMonth))
                  .map(p => (
                    <div key={p.id} style={{
                      padding: '14px 16px', borderRadius: 10,
                      background: 'var(--t-bg-elev)',
                      border: '1px solid var(--t-line)',
                      borderLeft: `3px solid ${bloomColor(p.flower_color)}`,
                    }}>
                      <div style={{ fontSize: 14, color: 'var(--t-text)', marginBottom: 4 }}>
                        {p.name}{p.variety && <span style={{ color: 'var(--t-text-dim)' }}> ({p.variety})</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--t-text-mute)' }}>
                        {p.plot_name} · Rząd {p.row_number}
                      </div>
                      {p.flower_color && (
                        <div style={{ marginTop: 6 }}>
                          <Chip tone="dim">{p.flower_color}</Chip>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              {bloomingPlants.filter(p => p.months.includes(hoveredMonth)).length === 0 && (
                <Meta>W tym miesiącu nic nie kwitnie w Twoim ogrodzie.</Meta>
              )}
            </ScheduleSection>
          )}
        </>
      )}
    </ScheduleShell>
  );
}
